import React, { useCallback } from 'react';
import { Loader2, Wrench } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { ProtectedComponent } from '@/components/ProtectedComponent';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { useAgentToolsQuery, useUnlinkToolMutation } from '@/query/agent.query';

import { Permissions } from '@/constants';

import { TransferCallCard } from '../transfer-call/transfer-call-card';

import { SelectToolContent } from './select-tool-dialog';
import { ToolRow } from './tool-row';

import useModalStore from '@/store/modal.store';
import { useToolsStore } from '@/store/tools.store';
import type { AgentTool } from '@/types/agent.type';

type AgentToolsTabProps = {
  agentId: number;
};

export const AgentToolsTab = React.memo(function AgentToolsTab({ agentId }: AgentToolsTabProps) {
  const navigate = useNavigate();
  const { data: tools = [], isLoading } = useAgentToolsQuery(agentId, true, {
    enabled: !!agentId,
  });

  const unlinkMutation = useUnlinkToolMutation(agentId);

  const handleUnlink = useCallback(
    (toolId: string) => unlinkMutation.mutate(toolId),
    [unlinkMutation]
  );

  const handleAddTool = useCallback(() => {
    useModalStore.getState().openModal(() => <SelectToolContent agentId={agentId} />);
  }, [agentId]);

  const handleOpenTool = useCallback(
    (tool: AgentTool) => {
      useToolsStore.getState().selectTool(tool);
      navigate('/tools');
    },
    [navigate]
  );

  return (
    <div className="space-y-6">
      <Card className="gap-0 rounded-2xl border-0 bg-muted p-1 shadow-none">
        <CardHeader className="px-5 py-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="mb-1 text-sm font-medium">Tools</CardTitle>
              <p className="text-sm text-muted-foreground">
                Link tools to let the agent call external APIs during a call.
              </p>
            </div>

            <ProtectedComponent requiredPermissions={[Permissions.AgentCreateUpdate]}>
              <Button type="button" variant="outline" onClick={handleAddTool}>
                <Wrench className="h-4 w-4" />
                Add tool
              </Button>
            </ProtectedComponent>
          </div>
        </CardHeader>

        <CardContent className="rounded-xl bg-card p-5 shadow-sm">
          <div className="space-y-3">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Loading tools...</span>
              </div>
            ) : tools.length === 0 ? (
              <div className="py-8 text-center">
                <Wrench className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground">No tools linked yet.</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Add a tool from the Tools page or link an existing one here.
                </p>
              </div>
            ) : (
              tools.map(tool => (
                <ToolRow
                  key={tool.id}
                  tool={tool}
                  onOpenTool={handleOpenTool}
                  onUnlink={handleUnlink}
                  isUnlinking={unlinkMutation.isPending}
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>
      <TransferCallCard agentId={agentId} />
    </div>
  );
});
